<template>
  <div ref="root">
    <transition :name="transition">
      <div
        v-show="isActive"
        class="ak-toast"
        :class="toastType"
        :aria-hidden="!isActive"
        role="alert"
      >
        <div class="ak-toast__body" v-html="message" />
        <div class="ak-toast__dismiss">
          <ak-close inverse @click="close()" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import config from '../../utils/config'
import Button from '../button/Button.vue'
import Icon from '../icon/Icon.vue'
import Close from '../close/Close.vue'
import { noticeProps, noticeMixin } from '../../mixins/noticeMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-toast />
   */
  name: 'AkToast',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Close.name]: Close,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...noticeProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['close'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const newDuration = ref(props.duration || config.defaultToastDuration)
    const root = ref(null)

    const mixin = noticeMixin({
      props,
      root,
      emit,
    })

    const toastType = computed(() => {
      switch (props.type) {
        case 'danger':
          return 'ak-toast--danger'
        case 'success':
          return 'ak-toast--success'
        case 'warning':
          return 'ak-toast--warning'
      }
      return ''
    })

    return {
      ...mixin,
      root,
      newDuration,
      toastType,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-toast {
  $self: &;
  max-width: 500px;
  display: flex;
  box-shadow: $base-shadow-large;
  align-items: center;
  padding: $spacing-small $spacing;
  border-radius: $base-border-radius;
  background-color: $color-grey-darker;
  overflow: hidden;
  color: #fff;

  &--danger {
    background-color: $color-danger;
  }
  &--success {
    background-color: $color-success;
  }
  &--warning {
    background-color: $color-warning;
  }

  &--inverse {
    background-color: #fff;
    color: $color-text;
  }

  &__body {
    flex-grow: 1;
  }

  &__action {
    flex-shrink: 1;
    margin-left: $spacing-small;
  }

  &__dismiss {
    flex-shrink: 0;
    margin-left: $spacing-small;
    margin-right: -$spacing-small;
    align-self: top;
  }
}
</style>
